import { CommonModule } from '@angular/common'
import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, inject, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core'
import { NavigationStart, Router, RouterModule } from '@angular/router'
import { Observable, Subscription } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { Message } from '../../services/message/message'
import { MessageScope } from '../../services/message/message-scope'
import { MessageType } from '../../services/message/message-type'
import { MessageService } from '../../services/message/message.service'

/**
 * A component that will dynamically render a `AlertComponent` for each alert type messages from
 * the `MessageService`.
 */
@Component({
  selector: 'gpei-alert-stack',
  templateUrl: 'alert-stack.component.html',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule
  ],
  schemas: [ CUSTOM_ELEMENTS_SCHEMA ]
})
export class AlertStackComponent implements OnInit, OnDestroy, AfterViewInit {

  /**
   * Collection of child `AlertComponent` rendered within the stack
   */
  @ViewChildren('alert', { read: ElementRef })
  alertRefs: QueryList<ElementRef>

  private _routerNavigateStartSubscription: Subscription

  private readonly _message = inject(MessageService)
  private readonly _router = inject(Router)

  /**
   * When the component is initialized, begin listening for changes to the router.
   *
   * When navigation for a route starts, it will clear all active alerts.
   */
  ngOnInit(): void {
    this._routerNavigateStartSubscription = this._router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => this._message.clear({ type: MessageType.ALERT, scope: MessageScope.PAGE }))
  }

  /**
   * When the component is destroyed remove all active subscriptions
   */
  ngOnDestroy(): void {
    if (this._routerNavigateStartSubscription) {
      this._routerNavigateStartSubscription.unsubscribe()
    }
  }

  /**
   * After the View of the Component has been initialized it will subscribe to changes
   * in the children components, and scroll to have any newly rendered AlertComponents
   * be in view.
   */
  ngAfterViewInit(): void {
    this.alertRefs.changes.subscribe(() => {
      if (this.alertRefs.length > 0) {
        this.alertRefs.first.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    })
  }

  /**
   * When an alert has been closed, remove the corresponding message from the `MessageService`
   *
   * @param value The message of the closed `AlertComponent`
   */
  onDismiss(value: Message): void {
    this._message.remove(value)
  }

  /**
   * An observable collection of messages
   */
  get alerts$(): Observable<Message[]> {
    return this._message
      .messages$
      .pipe(
        map(it => it.filter(message => message.type === MessageType.ALERT))
      )
  }

}
